/**
 * In-place energy scaling. energy=1.0 → no change. Samples are clamped
 * to [-1, 1] to prevent hard clipping when the int16 PCM is written.
 */
export function applyEnergyScale(pcm: Float32Array, energy: number): void {
  const e = Math.min(Math.max(energy, 0.5), 1.5);
  if (Math.abs(e - 1) < 1e-5) return;
  for (let i = 0; i < pcm.length; i++) {
    const v = pcm[i] * e;
    pcm[i] = v < -1 ? -1 : v > 1 ? 1 : v;
  }
}

import { shiftPitch } from './phaseVocoder';

/**
 * Pitch shift that preserves duration. Stage 12 (A1) delegates to a
 * dependency-free STFT phase vocoder — decoupling pitch from rate so
 * `pitch ∈ [0.85, 1.15]` truly changes pitch only. Returns a new
 * Float32Array of the SAME length as the input. `pitch = 1.0` returns
 * the input reference unchanged (fast path).
 *
 * The old linear-resample behaviour (pitch coupled to rate) is preserved
 * inside `linearResamplePitch` below for debugging / A/B testing.
 */
export function applyPitchScale(
  pcm: Float32Array,
  pitch: number,
): Float32Array {
  return shiftPitch(pcm, pitch);
}

/**
 * Stage 8 linear-resample pitch shifter, kept for debugging. Retains the
 * old behaviour where pitch > 1 shortens audio and pitch < 1 lengthens.
 */
export function linearResamplePitch(
  pcm: Float32Array,
  pitch: number,
): Float32Array {
  const p = Math.min(Math.max(pitch, 0.5), 1.5);
  if (Math.abs(p - 1) < 1e-4) return pcm;
  const outLen = Math.max(1, Math.round(pcm.length / p));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcF = i * p;
    const i0 = Math.floor(srcF);
    const i1 = Math.min(pcm.length - 1, i0 + 1);
    const frac = srcF - i0;
    out[i] = pcm[i0] * (1 - frac) + pcm[i1] * frac;
  }
  return out;
}
